/**
 * Post-processing of a page body that is already sanitized HTML (the renderer runs `sanitizePageHtml`), done once per body:
 * - every heading gets an id (DOMPurify drops ids that would clobber a document property, e.g. `# Title`), so the TOC can link it;
 * - the table of contents is read from those headings (both renderers, whatever slug rules they use);
 * - `::: tabs` blocks (`@mdit/plugin-tab` markup) become placeholders that the reader fills with the library `Tabs`;
 * - fenced code blocks get a placeholder for their Copy button.
 * Nothing here adds markup a reader could not already write: placeholders are empty `div`/`span`s with a data attribute.
 */

export interface TocEntry {
    level: number;
    label: string;
    /** The heading's id (without `#`). */
    id: string;
    children: TocEntry[];
}

export interface TabSet {
    labels: string[];
    /** Inner HTML of each panel, in label order (sanitized already: it is a fragment of the sanitized body). */
    panels: string[];
}

export interface CodeBlock {
    /** `shell`, `ts`… from `language-*`, when the fence named one. */
    language?: string;
    text: string;
}

export interface PreparedBody {
    /** Prefix of this body's placeholder ids (unique per call, so nested bodies never collide). */
    key: string;
    html: string;
    toc: TocEntry[];
    tabs: TabSet[];
    code: CodeBlock[];
}

export const TABS_HOST_ATTRIBUTE = "data-ark-tabs";
export const CODE_HOST_ATTRIBUTE = "data-ark-code";

const HEADINGS = "h1, h2, h3, h4, h5, h6";

let prepared = 0;

function headingText(el: Element): string {
    return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

/** Nests flat headings by level, like the old AMTableOfContents (a deeper heading becomes a child of the one before). */
export function nestHeadings(flat: { level: number; label: string; id: string }[]): TocEntry[] {
    const root: TocEntry = { level: 0, label: "", id: "", children: [] };
    const stack: TocEntry[] = [root];
    for (const heading of flat) {
        const node: TocEntry = { ...heading, children: [] };
        while (stack.length > 1 && heading.level <= stack[stack.length - 1].level) stack.pop();
        stack[stack.length - 1].children.push(node);
        stack.push(node);
    }
    return root.children;
}

/** The `@mdit/plugin-tab` block, when its buttons and panes line up; otherwise undefined (it stays stacked). */
function readTabSet(wrapper: Element): TabSet | undefined {
    const header = Array.from(wrapper.children).find((c) => c.classList.contains("tabs-tabs-header"));
    const container = Array.from(wrapper.children).find((c) => c.classList.contains("tabs-tabs-container"));
    if (!header || !container) return undefined;
    const buttons = Array.from(header.children).filter((c) => c.classList.contains("tabs-tab-button"));
    const panes = Array.from(container.children).filter((c) => c.classList.contains("tabs-tab-content"));
    if (buttons.length === 0 || buttons.length !== panes.length) return undefined;
    return {
        labels: buttons.map((b, i) => headingText(b) || `Tab ${i + 1}`),
        panels: panes.map((p) => p.innerHTML),
    };
}

function outermost(elements: Element[]): Element[] {
    return elements.filter((el) => !elements.some((other) => other !== el && other.contains(el)));
}

/** Parses a sanitized body and returns it with heading ids, its TOC, and the tab sets / code blocks the reader enhances. */
export function prepareBody(html: string): PreparedBody {
    const key = `b${++prepared}`;
    if (!html) return { key, html: "", toc: [], tabs: [], code: [] };

    const template = document.createElement("template");
    template.innerHTML = html;
    const root = template.content;

    // 1. ids and the TOC (headings inside the `[[toc]]` nav are not content headings: it has none, but be safe)
    const used = new Set(Array.from(root.querySelectorAll("[id]")).map((el) => el.id));
    let next = 1;
    const flat: { level: number; label: string; id: string }[] = [];
    root.querySelectorAll(HEADINGS).forEach((el) => {
        if (el.closest("nav.table-of-contents")) return;
        if (!el.id) {
            let id = `ark-section-${next++}`;
            while (used.has(id)) id = `ark-section-${next++}`;
            el.id = id;
            used.add(id);
        }
        const label = headingText(el);
        if (label) flat.push({ level: Number(el.tagName.substring(1)), label, id: el.id });
    });

    // 2. tabs: only the outermost sets (a nested set is handled when its panel renders)
    const tabs: TabSet[] = [];
    outermost(Array.from(root.querySelectorAll(".tabs-tabs-wrapper"))).forEach((wrapper) => {
        const set = readTabSet(wrapper);
        if (!set) return;
        const host = document.createElement("div");
        host.className = "ark-tabs-host";
        host.setAttribute(TABS_HOST_ATTRIBUTE, `${key}-${tabs.length}`);
        tabs.push(set);
        wrapper.replaceWith(host);
    });

    // 3. code blocks (outside tab sets: those are prepared with their panel)
    const code: CodeBlock[] = [];
    root.querySelectorAll("pre > code").forEach((el) => {
        const pre = el.parentElement!;
        if (pre.parentElement?.classList.contains("ark-code")) return;
        const language = Array.from(el.classList).find((c) => c.startsWith("language-"))?.substring("language-".length);
        const frame = document.createElement("div");
        frame.className = "ark-code";
        const head = document.createElement("div");
        head.className = "ark-code-head";
        head.setAttribute(CODE_HOST_ATTRIBUTE, `${key}-${code.length}`);
        if (language) head.setAttribute("data-language", language);
        pre.replaceWith(frame);
        frame.append(head, pre);
        code.push({ language, text: el.textContent ?? "" });
    });

    return { key, html: template.innerHTML, toc: nestHeadings(flat), tabs, code };
}

/** Number of headings in a TOC tree. */
export function countToc(entries: TocEntry[]): number {
    return entries.reduce((n, e) => n + 1 + countToc(e.children), 0);
}
